import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Plus, Trash2, Pencil } from "lucide-react";
import TableContainer from "Common/TableContainer";
import { Link, useNavigate } from "react-router-dom";
import DeleteModal from "Common/DeleteModal";
import Modal from "Common/Components/Modal";
import Select from "react-select";
import useSearchableOptions from "Common/hooks/useSearchableOptions";
import selectStyles from "Common/constants/selectStyles";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import Swal from "sweetalert2";
import { apiErrorMessage } from "Common/constants/apiErrors";
// ========== آدرس پایه API ==========
const BASE_URL = "https://portal-api.mahanpanel.com";

// کد کالا در سرور فقط رقم انگلیسی/ASCII است؛ اگر کاربر با کیبورد فارسی رقم بزند (۰۱۰۵۱۱۵۵)،
// بدون این تبدیل هیچ‌وقت با کدهای ذخیره‌شده مطابقت پیدا نمی‌کند.
const toEnglishDigits = (str: string): string =>
  str.replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d).toString()).replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d).toString());

// ========== تابع کمکی برای اطمینان از آرایه ==========
const ensureArray = (data: any): any[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === "object") {
    // کلیدهای رایج
    const keys = ["items", "data", "results", "list"];
    for (const key of keys) {
      if (data[key] && Array.isArray(data[key])) {
        return data[key];
      }
    }
    const values = Object.values(data);
    if (values.length === 1 && Array.isArray(values[0])) {
      return values[0];
    }
    return values;
  }
  return [];
};

// ========== اینترفیس‌ها ==========
interface MeasurementUnit {
  id: number;
  name: string;
}

interface ProductType {
  id: number;
  name: string;
}

interface Warehouse {
  id: number;
  code: string;
  name: string;
}

interface GoodsService {
  id: number;
  code: string;
  title: string;
  specification: string | null;
  measurement_unit_id: number;
  measurement_unit_name: string;
  product_type_id: number;
  product_type_name: string;
  sales_group: string | null;
  is_active: boolean;
  warehouse_id: number;
  warehouse_code: string;
  warehouse_name: string;
  tax_rate: string | number;
  created_at: string;
  created_at_jalali: string;
  updated_at: string;
  updated_at_jalali: string;
}

// ========== کامپوننت اصلی ==========
const Index = () => {
  const navigate = useNavigate();

  // State داده‌ها
  const [data, setData] = useState<GoodsService[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [reload, setReload] = useState<boolean>(false);

  // State داده‌های اولیه (برای انتخاب‌ها)
  const [units, setUnits] = useState<MeasurementUnit[]>([]);
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);

  // State صفحه‌بندی
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<"default" | "asc" | "desc">("default");
  const changeServerSort = useCallback((nextSortBy: string | undefined, nextSortOrder: "default" | "asc" | "desc") => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setCurrentPage(1);
  }, []);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filters, setFilters] = useState<{
    measurement_unit_id?: number;
    product_type_id?: number;
    warehouse_id?: number;
    is_active?: boolean;
  }>({});

  // با هر تغییر جست‌وجو/فیلتر باید به صفحه‌ی ۱ برگردیم؛ وگرنه اگر کاربر روی صفحه‌ی ۳ باشد و
  // نتیجه‌ی جست‌وجوی جدید کمتر از ۳ صفحه داشته باشد، سرور برای آن صفحه چیزی برنمی‌گرداند و به
  // نظر می‌رسد جست‌وجو «داده‌ای پیدا نکرده».
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  // Modal states
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<GoodsService | null>(null);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  // ========== دریافت داده‌های اولیه ==========
  useEffect(() => {
    const fetchInitialData = async () => {
      setInitialLoading(true);
      try {
        const token = localStorage.getItem("app_at_mahan");
        const headers = { Authorization: `Bearer ${token}` };

        const [unitsRes, typesRes, warehousesRes] = await Promise.all([
          axios.get(`https://portal-api.mahanpanel.com/measurement-units/`, { headers }),
          axios.get(`https://portal-api.mahanpanel.com/product-types/`, { headers }),
          axios.get(`https://portal-api.mahanpanel.com/warehouses/`, { headers }),
        ]);

        setUnits(ensureArray(unitsRes.data));
        setProductTypes(ensureArray(typesRes.data));
        setWarehouses(ensureArray(warehousesRes.data));
      } catch (error: any) {
        console.error("Error fetching initial data:", error);
        if (error.response?.status === 401) {
          navigate("/login");
        }
      } finally {
        setInitialLoading(false);
      }
    };
    fetchInitialData();
  }, [navigate]);

  // ========== دریافت لیست کالا و خدمات ==========
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("app_at_mahan");
        const params: any = {
          page: currentPage,
          size: pageSize,
          ...(sortOrder === "default" ? { sort_order: "default" } : { sort_by: sortBy, sort_order: sortOrder }),
        };
        if (searchTerm) params.search = toEnglishDigits(searchTerm.trim());
        if (filters.measurement_unit_id) params.measurement_unit_id = filters.measurement_unit_id;
        if (filters.product_type_id) params.product_type_id = filters.product_type_id;
        if (filters.warehouse_id) params.warehouse_id = filters.warehouse_id;
        if (filters.is_active !== undefined) params.is_active = filters.is_active;

        const res = await axios.get(`https://portal-api.mahanpanel.com/goods-services/`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });

        const responseData = res.data;
        const items = responseData?.items ? ensureArray(responseData.items) : ensureArray(responseData);
        setData(items);
        const total = responseData?.total ?? items.length;
        setTotalItems(total);
        setTotalPages(responseData?.pages || Math.max(Math.ceil(total / pageSize), 1));
      } catch (error: any) {
        console.error("Error fetching goods/services:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        Swal.fire("خطا", apiErrorMessage(error, "مشکلی در دریافت لیست کالا و خدمات رخ داده است."), "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reload, currentPage, pageSize, searchTerm, filters, navigate, sortBy, sortOrder]);

  // ========== حذف ==========
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.delete(`${BASE_URL}/goods-services/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 200) {
        Swal.fire("عملیات موفق", "کالا/خدمت با موفقیت حذف شد", "success");
        setReload(!reload);
        setDeleteModal(false);
        setDeleteTarget(null);
      }
    } catch (error: any) {
      console.error("Delete error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      Swal.fire("خطا", apiErrorMessage(error, "خطا در حذف کالا/خدمت"), "error");
    }
  };

  // ========== فرم ثبت و ویرایش ==========
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      code: "",
      title: "",
      specification: "",
      measurement_unit_id: "",
      product_type_id: "",
      sales_group: "",
      is_active: true,
      warehouse_id: "",
      tax_rate: "",
    },
    validationSchema: Yup.object({
      code: Yup.string().required("لطفاً کد کالا را وارد کنید"),
      title: Yup.string().required("لطفاً عنوان کالا را وارد کنید"),
      measurement_unit_id: Yup.string().required("لطفاً واحد اندازه‌گیری را انتخاب کنید"),
      product_type_id: Yup.string().required("لطفاً نوع محصول را انتخاب کنید"),
      warehouse_id: Yup.string().required("لطفاً انبار را انتخاب کنید"),
      tax_rate: Yup.number()
        .typeError("مقدار باید عدد باشد")
        .min(0, "حداقل ۰")
        .max(100, "حداکثر ۱۰۰")
        .nullable(),
    }),
    onSubmit: async (values) => {
      const payload = {
        code: values.code,
        title: values.title,
        specification: values.specification || undefined,
        measurement_unit_id: Number(values.measurement_unit_id),
        product_type_id: Number(values.product_type_id),
        sales_group: values.sales_group || undefined,
        is_active: values.is_active,
        warehouse_id: Number(values.warehouse_id),
        tax_rate: values.tax_rate ? parseFloat(values.tax_rate) : 0,
      };

      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        let res;
        if (editMode && editingId) {
          res = await axios.patch(`${BASE_URL}/goods-services/${editingId}`, payload, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          if (res.status === 200) {
            Swal.fire("عملیات موفق", `کالا/خدمت "${values.title}" بروزرسانی شد`, "success");
            setReload(!reload);
            closeModal();
          }
        } else {
          res = await axios.post(`${BASE_URL}/goods-services/`, payload, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          if (res.status === 201) {
            Swal.fire("عملیات موفق", `کالا/خدمت "${values.title}" ثبت شد`, "success");
            setReload(!reload);
            closeModal();
          }
        }
      } catch (error: any) {
        console.error("Submit error:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        if (error.response?.status === 422) {
          // پیغام خطای سرور انگلیسی است؛ apiErrorMessage آن را به جمله‌ی فارسی
          // گویا تبدیل می‌کند (تکراری بودن کد ملی/شماره همراه، فیلد الزامی و ...).
          let msg = apiErrorMessage(error, "اطلاعات وارد شده نامعتبر است");
          Swal.fire("خطا", msg, "error");
        } else {
          Swal.fire("خطا", apiErrorMessage(error, "مشکل در ثبت یا ویرایش کالا/خدمت"), "error");
        }
      }
    },
  });

  // ========== باز کردن مودال برای ویرایش ==========
  const handleEdit = (row: GoodsService) => {
    setEditMode(true);
    setEditingId(row.id);
    validation.setValues({
      code: row.code || "",
      title: row.title || "",
      specification: row.specification || "",
      measurement_unit_id: String(row.measurement_unit_id),
      product_type_id: String(row.product_type_id),
      sales_group: row.sales_group || "",
      is_active: row.is_active,
      warehouse_id: String(row.warehouse_id),
      tax_rate: row.tax_rate ? String(row.tax_rate) : "",
    });
    setShowModal(true);
  };

  // ========== باز کردن مودال برای ثبت جدید ==========
  const openCreateModal = () => {
    setEditMode(false);
    setEditingId(null);
    validation.resetForm();
    setShowModal(true);
  };

  // ========== بستن مودال ==========
  const closeModal = () => {
    setShowModal(false);
    setEditMode(false);
    setEditingId(null);
    validation.resetForm();
  };

  // ========== Searchable Options ==========
  const unitSearch = useSearchableOptions(units, (u) => u.name, (u) => String(u.id));
  const productTypeSearch = useSearchableOptions(productTypes, (p) => p.name, (p) => String(p.id));
  const warehouseSearch = useSearchableOptions(
    warehouses,
    (w) => `${w.code} - ${w.name}`,
    (w) => String(w.id)
  );

  // دریافت نام‌های انتخاب‌شده برای نمایش در Select
  const selectedUnitName = units.find((u) => String(u.id) === validation.values.measurement_unit_id)?.name;
  const selectedProductTypeName = productTypes.find((p) => String(p.id) === validation.values.product_type_id)?.name;
  const selectedWarehouseLabel = warehouses.find((w) => String(w.id) === validation.values.warehouse_id)
    ? `${warehouses.find((w) => String(w.id) === validation.values.warehouse_id)?.code} - ${warehouses.find((w) => String(w.id) === validation.values.warehouse_id)?.name}`
    : "";

  // ========== ستون‌های جدول ==========
  const columns = useMemo(
    () => [
      {
        header: "شناسه",
        accessorKey: "id",
        cell: (cell: any) => <span>{cell.getValue()}</span>,
      },
      {
        header: "کد کالا",
        accessorKey: "code",
      },
      {
        header: "عنوان کالا",
        accessorKey: "title",
        cell: (cell: any) => (
          <Link
            to="#!"
            className="transition-all duration-150 ease-linear text-custom-500 hover:text-custom-600"
          >
            {cell.getValue()}
          </Link>
        ),
      },
      {
        header: "واحد",
        accessorKey: "measurement_unit_name",
      },
      {
        header: "نوع محصول",
        accessorKey: "product_type_name",
      },
      {
        header: "کد انبار",
        accessorKey: "warehouse_code",
      },
      {
        header: "نام انبار",
        accessorKey: "warehouse_name",
      },
      {
        header: "وضعیت",
        accessorKey: "is_active",
        cell: (cell: any) => (
          <span className={cell.getValue() ? "text-green-500" : "text-red-500"}>
            {cell.getValue() ? "فعال" : "غیرفعال"}
          </span>
        ),
      },
      {
        header: "نرخ مالیات",
        accessorKey: "tax_rate",
        cell: (cell: any) => (cell.getValue() ? `${cell.getValue()}%` : "-"),
      },
      {
        header: "عملیات",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const row: GoodsService = cell.row.original;
          return (
            <div className="flex gap-3">
              <Link to="#!" onClick={() => handleEdit(row)}>
                <Pencil className="size-4 text-blue-500 hover:text-blue-700" />
              </Link>
              <Link to="#!" onClick={() => { setDeleteTarget(row); setDeleteModal(true); }}>
                <Trash2 className="size-4 text-red-500 hover:text-red-700" />
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );

  // ========== رندر ==========
  return (
    <React.Fragment>
      <BreadCrumb pageTitle="کالا و خدمات" title="داده‌های اولیه" />
      <DeleteModal
        show={deleteModal}
        onHide={() => {
          setDeleteModal(false);
          setDeleteTarget(null);
        }}
        onDelete={handleDelete}
      />

      <div className="card">
        <div className="card-body">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <h6 className="text-15 grow">
              مجموع کالا و خدمات (<b className="total-Employs">{totalItems}</b>)
            </h6>
            {/* فیلتر‌های ساده - می‌توانید گسترش دهید */}
            <div className="flex gap-2 items-center">
              <input
                type="text"
                placeholder="جستجو در کد یا عنوان..."
                className="form-input text-sm w-48"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                onClick={() => setReload(!reload)}
                className="btn bg-slate-200 dark:bg-zink-500 text-xs px-3 py-1.5"
              >
                جستجو
              </button>
            </div>
            <div className="shrink-0">
              <button
                onClick={openCreateModal}
                type="button"
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600"
              >
                <span className="align-middle rtl">ثبت کالا / خدمت جدید</span>{" "}
                <Plus className="inline-block size-4 rtl" />
              </button>
            </div>
          </div>

          {loading || initialLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-custom-500 border-t-transparent"></div>
            </div>
          ) : data.length > 0 ? (
            <TableContainer
              isPagination={true}
              columns={columns}
              data={data}
              serverSorting={{ sortBy, sortOrder, onChange: changeServerSort }}
              customPageSize={pageSize}
              serverPagination={{
                currentPage,
                totalPages,
                onPageChange: setCurrentPage,
                pageSize,
                onPageSizeChange: (size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                },
              }}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80"
              PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
            />
          ) : (
            <LoadingCounter count={1} text="هیچ کالا یا خدمتی ثبت نشده است" />
          )}
        </div>
      </div>

      {/* ========== مودال ثبت/ویرایش ========== */}
      <Modal
        show={showModal}
        onHide={closeModal}
        modal-center="true"
        dialogClassName="w-screen md:w-[36rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500"
        >
          <Modal.Title className="text-16">
            {editMode ? "ویرایش کالا / خدمت" : "اضافه کردن کالا / خدمت"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form onSubmit={validation.handleSubmit} id="goods-form">
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {/* کد */}
              <div>
                <label className="block text-base font-medium mb-1">کد کالا</label>
                <input
                  type="text"
                  className="form-input w-full"
                  name="code"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.code}
                  placeholder="کد کالا"
                />
                {validation.touched.code && validation.errors.code && (
                  <p className="text-red-400 mt-1 text-sm">{validation.errors.code}</p>
                )}
              </div>

              {/* عنوان */}
              <div>
                <label className="block text-base font-medium mb-1">عنوان کالا</label>
                <input
                  type="text"
                  className="form-input w-full"
                  name="title"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.title}
                  placeholder="عنوان کالا"
                />
                {validation.touched.title && validation.errors.title && (
                  <p className="text-red-400 mt-1 text-sm">{validation.errors.title}</p>
                )}
              </div>

              {/* واحد اندازه‌گیری */}
              <div>
                <label className="block text-base font-medium mb-1">واحد اندازه‌گیری</label>
                <Select
                  options={unitSearch.options}
                  onInputChange={unitSearch.onInputChange}
                  value={
                    validation.values.measurement_unit_id
                      ? {
                        value: validation.values.measurement_unit_id,
                        label: selectedUnitName || "",
                      }
                      : null
                  }
                  onChange={(opt: any) =>
                    validation.setFieldValue("measurement_unit_id", opt?.value || "")
                  }
                  placeholder="جست‌وجوی واحد..."
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable
                  noOptionsMessage={() => "موردی یافت نشد"}
                />
                {validation.touched.measurement_unit_id && validation.errors.measurement_unit_id && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.measurement_unit_id === "string"
                      ? validation.errors.measurement_unit_id
                      : ""}
                  </p>
                )}
              </div>

              {/* نوع محصول */}
              <div>
                <label className="block text-base font-medium mb-1">نوع محصول</label>
                <Select
                  options={productTypeSearch.options}
                  onInputChange={productTypeSearch.onInputChange}
                  value={
                    validation.values.product_type_id
                      ? {
                        value: validation.values.product_type_id,
                        label: selectedProductTypeName || "",
                      }
                      : null
                  }
                  onChange={(opt: any) =>
                    validation.setFieldValue("product_type_id", opt?.value || "")
                  }
                  placeholder="جست‌وجوی نوع محصول..."
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable
                  noOptionsMessage={() => "موردی یافت نشد"}
                />
                {validation.touched.product_type_id && validation.errors.product_type_id && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.product_type_id === "string"
                      ? validation.errors.product_type_id
                      : ""}
                  </p>
                )}
              </div>

              {/* انبار */}
              <div>
                <label className="block text-base font-medium mb-1">انبار</label>
                <Select
                  options={warehouseSearch.options}
                  onInputChange={warehouseSearch.onInputChange}
                  value={
                    validation.values.warehouse_id
                      ? {
                        value: validation.values.warehouse_id,
                        label: selectedWarehouseLabel || "",
                      }
                      : null
                  }
                  onChange={(opt: any) =>
                    validation.setFieldValue("warehouse_id", opt?.value || "")
                  }
                  placeholder="جست‌وجوی انبار..."
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable
                  noOptionsMessage={() => "موردی یافت نشد"}
                />
                {validation.touched.warehouse_id && validation.errors.warehouse_id && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.warehouse_id === "string"
                      ? validation.errors.warehouse_id
                      : ""}
                  </p>
                )}
              </div>

              {/* گروه فروش (اختیاری) */}
              <div>
                <label className="block text-base font-medium mb-1">گروه فروش (اختیاری)</label>
                <input
                  type="text"
                  className="form-input w-full"
                  name="sales_group"
                  onChange={validation.handleChange}
                  value={validation.values.sales_group}
                  placeholder="گروه فروش"
                />
              </div>

              {/* نرخ مالیات (اختیاری) */}
              <div>
                <label className="block text-base font-medium mb-1">نرخ مالیات (%)</label>
                <input
                  type="number"
                  className="form-input w-full"
                  name="tax_rate"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.tax_rate}
                  placeholder="۰"
                  min="0"
                  max="100"
                />
                {validation.touched.tax_rate && validation.errors.tax_rate && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.tax_rate === "string"
                      ? validation.errors.tax_rate
                      : ""}
                  </p>
                )}
              </div>

              {/* مشخصات (اختیاری) */}
              <div className="xl:col-span-2">
                <label className="block text-base font-medium mb-1">مشخصات (اختیاری)</label>
                <input
                  type="text"
                  className="form-input w-full"
                  name="specification"
                  onChange={validation.handleChange}
                  value={validation.values.specification}
                  placeholder="مشخصات کالا"
                />
              </div>

              {/* فعال */}
              <div className="xl:col-span-2 flex items-center gap-2">
                <label className="text-base font-medium">فعال</label>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={validation.values.is_active}
                  onChange={validation.handleChange}
                  className="form-checkbox h-4 w-4"
                />
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className="flex justify-center p-4 border-t dark:border-zink-500">
          <button
            type="button"
            onClick={() => validation.handleSubmit()}
            className="btn min-w-[9rem] px-8 py-2.5 mt-2 font-bold text-white bg-custom-500 border-custom-500 hover:bg-custom-600"
          >
            {editMode ? "ویرایش" : "ثبت"}
          </button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default Index;
